import type { ColumnsType, ColumnType } from "@similarweb/pro-logic-components";
import { TextCell, StateChangeCell } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Flex } from "@similarweb/ui-components/foundations";

import { chosenSites } from "app/common/services/chosenSites";
import { TrafficShareWithVisits, UrlCellWebsiteAnalysis, WaKeywordPosition } from "app/components/React/Table/cells";
import { CompareBarCell } from "app/components/React/Table/cells/CompareBarCell";
import { getIndexCellPropsForTableV2 } from "app/components/React/Table/cells/IndexCell";
import {
    NO_DATA_SIGN,
    THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE,
    THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE,
} from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { minAbbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import {
    StyledEnrichButton,
    StyledEnrichButtonNumber,
} from "app/pages/keyword-research/website-analysis/pages/landing-pages/StyledComponents";
import { SerpTableCellSingle } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellSingle";
import { allTrackers } from "app/services/track/track";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

import type { ITableOptions } from "../website-keywords/enrichedTable/types";
import { formatPositionChange } from "../website-keywords/table/changeValueFormatters";
import { getCustomContainerForTopUrlsTooltip } from "../website-keywords/WebsiteKeywordsPageUtillities2";

import type {
    ITransformedEnrichedKeywordsData,
    IClicksShare,
    ISerpFeatures,
    ITransformedTopicsData,
    IClicksChange,
} from "./types";
import { EnrichedTabs } from "./types";

type TopicColumnData = Pick<ITransformedTopicsData, "Topic">;

export const getTopicColumn = (totalCount: number) =>
    ({
        dataIndex: "Topic",
        title: `${translate("website.topics.table.topic.title")} (${totalCount})`,
        get tooltipSubtitle() {
            return translate("website.topics.table.topic.tooltip");
        },
        render: (rowCellData: string) => <TextCell>{rowCellData}</TextCell>,
        width: 150,
        fixed: true,
    }) satisfies ColumnType<TopicColumnData>;

type KeywordsColumnData = Pick<ITransformedTopicsData, "Keywords" | "key">;

export const getKeywordsColumn = (toggleExpandRow?: (key: number, tabId: EnrichedTabs) => void) =>
    ({
        dataIndex: "Keywords",
        get title() {
            return translate("website.topics.table.keywords.title");
        },
        get tooltipSubtitle() {
            return translate("website.topics.table.keywords.tooltip");
        },
        width: 88,
        align: "right",
        onCell: (record) => {
            return {
                onClick: () => {
                    if (record.Keywords > 0 && toggleExpandRow) {
                        toggleExpandRow(record.key, EnrichedTabs.Keywords);
                    }
                },
            };
        },
        render: (rowCellData: number) => {
            const isDisabled = !rowCellData;
            const handleClick = () => {
                if (!isDisabled) {
                    allTrackers.dispatchTrackEvent("Button", "click", "Expand Topic");
                }
            };
            return (
                <Flex justifyContent="flex-end">
                    <StyledEnrichButton
                        height={24}
                        onClick={isDisabled ? undefined : handleClick}
                        iconName="chev-down"
                        isDisabled={isDisabled}
                    >
                        <StyledEnrichButtonNumber isDisabled={isDisabled}>
                            {minAbbrNumberFilter()(rowCellData)}
                        </StyledEnrichButtonNumber>
                    </StyledEnrichButton>
                </Flex>
            );
        },
    }) satisfies ColumnType<KeywordsColumnData>;

type KeywordColumnData = Pick<ITransformedEnrichedKeywordsData, "Keyword">;

export const KEYWORD_COLUMN = {
    dataIndex: "Keyword",
    get title() {
        return translate("website.topics.enrich.table.keywords.title");
    },
    get tooltipSubtitle() {
        return translate("website.topics.enrich.table.keywords.tooltip");
    },
    render: (rowCellData: string) => <TextCell>{rowCellData}</TextCell>,
    width: 150,
    fixed: true,
} satisfies ColumnType<KeywordColumnData>;

type PositionColumnData = Pick<ITransformedEnrichedKeywordsData, "Position">;

export const POSITION_COLUMN = {
    dataIndex: "Position",
    get title() {
        return translate("website.topics.enrich.table.position.title");
    },
    get tooltipSubtitle() {
        return translate("website.topics.enrich.table.position.tooltip");
    },
    width: 70,
    align: "right",
    render: (rowCellData, rowData) => (
        // @ts-expect-error FIXME: Type '{ field: string; row: PositionColumnData; }' is missing 50+ more props
        <WaKeywordPosition
            field="Position"
            row={rowData}
            {...rowCellData}
        />
    ),
    sorter: true,
} satisfies ColumnType<PositionColumnData>;

type PositionChangeColumnData = Pick<ITransformedEnrichedKeywordsData, "positionChange">;

export const POSITION_CHANGE_COLUMN = {
    dataIndex: "positionChange",
    get title() {
        return translate("website.topics.enrich.table.position.change.title");
    },
    get tooltipSubtitle() {
        return translate("website.topics.enrich.table.position.change.tooltip");
    },
    width: 95,
    align: "right",
    render: (rowCellData) => {
        const { value, formattedValue } = formatPositionChange(rowCellData.Position, rowCellData.PreviousPosition);

        return (
            <StateChangeCell
                value={value}
                formattedValue={formattedValue}
                noChangeSign={"0"}
            />
        );
    },
} satisfies ColumnType<PositionChangeColumnData>;

type TopUrlColumnData = Pick<ITransformedEnrichedKeywordsData, "TopUrl" | "Keyword">;

export const getTopUrlColumn = (targetState?: string) =>
    ({
        dataIndex: "TopUrl",
        get title() {
            return translate("website.topics.enrich.table.topUrl.title");
        },
        get tooltipSubtitle() {
            return translate("website.topics.enrich.table.topUrl.tooltip");
        },
        width: 130,
        align: "left",
        render: (rowCellData, rowData) => (
            <UrlCellWebsiteAnalysis
                value={rowCellData}
                customContainer={getCustomContainerForTopUrlsTooltip(
                    [{ Key: rowData.Keyword, Value: rowCellData }],
                    rowData.Keyword,
                    targetState,
                )}
            />
        ),
    }) satisfies ColumnType<TopUrlColumnData>;

type SerpFeaturesColumnData = {
    serpFeatures: ISerpFeatures;
};

export const getSerpFeaturesColumn = (tableOptions: ITableOptions) =>
    ({
        dataIndex: "serpFeatures",
        get title() {
            return translate("serp.column.name");
        },
        get tooltipSubtitle() {
            return translate(`website.topics.enrich.table.serp.tootltip`);
        },
        width: 100,
        align: "left",
        render: (rowCellData: ISerpFeatures) => {
            tableOptions.sitesForSerpColumn = chosenSites.sitelistForLegend();
            tableOptions.serpFeatureItemBackgroundColor = colorsPalettes.sky["200"];
            return (
                <SerpTableCellSingle
                    row={rowCellData}
                    tableOptions={tableOptions}
                />
            );
        },
    }) satisfies ColumnType<SerpFeaturesColumnData>;

export const getIndexColumn = <T,>(currentPage: number, pageSize: number): ColumnType<T> => ({
    ...getIndexCellPropsForTableV2<T>(currentPage, pageSize),
});

type VolumeColumnData = {
    Volume: number;
};

export const VOLUME_COLUMN = {
    dataIndex: "Volume",
    get title() {
        return translate("website.topics.table.total.volume.title");
    },
    get tooltipSubtitle() {
        return translate("website.topics.table.total.volume.tooltip");
    },
    width: 88,
    align: "right",
    render: (rowCellData: number) => (
        <TextCell>{minAbbrNumberFilter()(rowCellData, THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE)}</TextCell>
    ),
    sorter: true,
} satisfies ColumnType<VolumeColumnData>;

type ClicksShareColumnData = {
    ClicksShare: IClicksShare;
    key: number;
};

export const getClicksShareColumn = (
    selectedPageTab: string,
    toggleExpandRow?: (key: number, tabId: EnrichedTabs) => void,
) =>
    ({
        dataIndex: "ClicksShare",
        get title() {
            return translate("website.topics.table.clicks.title", {
                selectedPageTab: EKeywordTabs[EKeywordTabs[selectedPageTab]],
            });
        },
        get tooltipSubtitle() {
            return translate("website.topics.table.clicks.tooltip");
        },
        onCell: (record) => {
            return {
                onClick: () => {
                    if (toggleExpandRow) {
                        toggleExpandRow(record.key, EnrichedTabs.Trends);
                    }
                },
            };
        },
        render: (rowCellData: IClicksShare) => {
            const isDisabled = !rowCellData;
            const handleClick = () => {
                if (!isDisabled) {
                    allTrackers.dispatchTrackEvent("Button", "click", "Expand Topic");
                }
            };
            return (
                <Flex justifyContent="space-between">
                    <TrafficShareWithVisits
                        row={{ totalClicks: rowCellData.totalClicks }}
                        value={rowCellData.clicksShare}
                        applyAbbrNumberFilter
                        undefinedSign={NO_DATA_SIGN}
                        totalVisitsRowProp="totalClicks"
                        thresholdForTotalFilter={THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE}
                        thresholdTrafficShareWithVisitsPercentage={THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE}
                        displayLessThanThreshold={true}
                    />
                    {toggleExpandRow ? (
                        <StyledEnrichButton
                            height={24}
                            onClick={isDisabled ? undefined : handleClick}
                            iconName="chev-down"
                            isDisabled={isDisabled}
                        >
                            <StyledEnrichButtonNumber isDisabled={isDisabled}>
                                {translate("website.topics.table.clicks.enrich.button.title")}
                            </StyledEnrichButtonNumber>
                        </StyledEnrichButton>
                    ) : null}
                </Flex>
            );
        },
        width: 170,
        sorter: true,
    }) satisfies ColumnType<ClicksShareColumnData>;

type ClicksChangeColumnData = {
    ClicksChange: IClicksChange;
};

export const CLICKS_CHANGE_COLUMN = {
    dataIndex: "ClicksChange",
    get title() {
        return translate("website.topics.table.change.title");
    },
    get tooltipSubtitle() {
        return translate("website.topics.table.change.tooltip");
    },
    width: 60,
    align: "right",
    render: (rowCellData) => {
        return (
            <StateChangeCell
                value={rowCellData}
                formattedValue={rowCellData.changeValueFormatted}
            />
        );
    },
    sorter: true,
} satisfies ColumnType<ClicksChangeColumnData>;

type OrganicShareColumnData = {
    OrganicShare: number;
};

export const ORGANIC_SHARE_COLUMN = {
    dataIndex: "OrganicShare",
    get title() {
        return translate("website.topics.table.sourceType.share.title");
    },
    get tooltipSubtitle() {
        return translate("website.topics.table.sourceType.share.tooltip");
    },
    width: 120,
    align: "left",
    fixed: "right",
    render: (rowCellData: number) => (
        <CompareBarCell
            isSourceType={true}
            value={rowCellData}
            shouldAddRightSpace={true}
        />
    ),
} satisfies ColumnType<OrganicShareColumnData>;

type ZeroClicksColumnData = Pick<ITransformedEnrichedKeywordsData, "ZeroClicks">;

export const ZERO_CLICKS_COLUMN = {
    dataIndex: "ZeroClicks",
    get title() {
        return translate("website.topics.table.zero.click.title");
    },
    get tooltipSubtitle() {
        return translate("website.topics.table.zero.click.tooltip");
    },
    width: 88,
    align: "right",
    render: (rowCellData: number) => <TextCell>{percentageSignFilter()(rowCellData, 2)}</TextCell>,
} satisfies ColumnType<ZeroClicksColumnData>;

export const getClicksChangeGroup = (
    toggleExpandRow: ((key: number, tabId: EnrichedTabs) => void) | undefined,
    selectedPageTab: string,
) =>
    ({
        get title() {
            return translate("website.topics.table.clicks.change.title");
        },
        width: 263,
        key: "clicksChange",
        align: "center",
        children: [
            getClicksShareColumn(selectedPageTab, toggleExpandRow),
            { ...CLICKS_CHANGE_COLUMN },
            ...(selectedPageTab === EKeywordTabs[EKeywordTabs.Total] ? [{ ...ORGANIC_SHARE_COLUMN }] : []),
        ],
    }) satisfies ColumnType<ClicksShareColumnData & ClicksChangeColumnData & OrganicShareColumnData>;

export const applySorting = <T,>(
    columns: ColumnsType<T>,
    sortConfig?: { sort: keyof T; asc: boolean },
): ColumnsType<T> => {
    if (!sortConfig) {
        return columns;
    }

    return columns.map((col) => {
        if (col.children) {
            col.children = col.children.map((child) => {
                if (child.dataIndex === sortConfig.sort) {
                    return { ...child, sortOrder: sortConfig.asc ? "ascend" : "descend" };
                }
                return child;
            });
            return col;
        } else if (col.dataIndex === sortConfig.sort) {
            return { ...col, sortOrder: sortConfig.asc ? "ascend" : "descend" };
        }
        return col;
    });
};
